/**
 * KeyVaultReferencesForm Component
 *
 * Form for managing Key Vault secret references.
 *
 * Feature: 017-backup-infrastructure-setup
 * Task: T033 - Create KeyVaultReferencesForm component
 * Constitution: v1.8.0 (Multi-Subscription SQL MI Edition)
 *
 * Requirements:
 * - FR-007: Key Vault secret references only
 * - FR-008: Validate Key Vault connectivity
 */

import React, { useState } from 'react';
import { KeyVaultReferenceDto, KeyVaultValidationResult, ValidationStatus } from '../types';
import {
  useKeyVaultReferences,
  useUpsertKeyVaultReference,
  useDeleteKeyVaultReference,
  useValidateKeyVaultReferences,
} from '../hooks/useInfrastructure';
import {
  DualScrollTable,
  usePagination,
  PaginationControls,
} from '../../../components/common/TableControls';
import { Modal, Button } from '../../../components/ui';
import { formatEastern } from '@/utils/dateFormatter';

interface KeyVaultReferencesFormProps {
  keyVaultUrl?: string;
  onUpdate?: () => void;
}

const getStatusColor = (status?: ValidationStatus): string => {
  switch (status) {
    case 'valid':
      return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
    case 'invalid':
      return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

const getStatusIcon = (status?: ValidationStatus): string => {
  switch (status) {
    case 'valid':
      return '✓';
    case 'invalid':
      return '✗';
    default:
      return '?';
  }
};

const COMMON_SECRET_PURPOSES = [
  { value: 'storage-connection-string', label: 'Storage Connection String', required: true },
  { value: 'sql-connection-string', label: 'SQL Connection String', required: true },
  { value: 'fabric-client-secret', label: 'Fabric Client Secret', required: false },
  { value: 'acr-password', label: 'ACR Password', required: false },
  { value: 'notification-webhook', label: 'Notification Webhook URL', required: false },
];

export const KeyVaultReferencesForm: React.FC<KeyVaultReferencesFormProps> = ({
  keyVaultUrl,
  onUpdate,
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingPurpose, setEditingPurpose] = useState<string | null>(null);
  const [newReference, setNewReference] = useState({
    purpose: '',
    secretName: '',
    description: '',
    isRequired: false,
  });
  const [formError, setFormError] = useState<string | null>(null);

  const { data: referencesData, isLoading, error, refetch } = useKeyVaultReferences();
  const upsertMutation = useUpsertKeyVaultReference();
  const deleteMutation = useDeleteKeyVaultReference();
  const validateMutation = useValidateKeyVaultReferences();

  const references = referencesData?.items || [];

  const { page, setPage, pageSize, changeSize, total, totalPages, pageItems, start } =
    usePagination(references);

  const handleAddReference = async () => {
    if (!newReference.purpose.trim()) {
      setFormError('Purpose is required');
      return;
    }
    if (!newReference.secretName.trim()) {
      setFormError('Secret name is required');
      return;
    }

    const secretNameRegex = /^[a-zA-Z0-9-]{1,127}$/;
    if (!secretNameRegex.test(newReference.secretName)) {
      setFormError('Secret name must be 1-127 alphanumeric characters or dashes');
      return;
    }

    setFormError(null);

    try {
      await upsertMutation.mutateAsync({
        purpose: newReference.purpose,
        reference: {
          secretName: newReference.secretName,
          description: newReference.description || null,
          isRequired: newReference.isRequired,
        },
      });
      setNewReference({ purpose: '', secretName: '', description: '', isRequired: false });
      setShowAddForm(false);
      refetch();
      onUpdate?.();
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Failed to add reference');
    }
  };

  const handleDeleteReference = async (purpose: string) => {
    if (!confirm(`Delete reference for "${purpose}"?`)) return;

    try {
      await deleteMutation.mutateAsync(purpose);
      refetch();
      onUpdate?.();
    } catch (err) {
      console.error('Failed to delete reference:', err);
    }
  };

  const handleValidateAll = async () => {
    try {
      await validateMutation.mutateAsync();
      refetch();
    } catch (err) {
      console.error('Failed to validate references:', err);
    }
  };

  if (isLoading) {
    return (
      <div className='bg-[var(--color-bg-card)] rounded-lg shadow-sm border border-[var(--color-border)] p-6'>
        <div className='animate-pulse space-y-4'>
          <div className='h-4 bg-[var(--color-bg-subtle)] rounded w-1/3'></div>
          <div className='h-32 bg-[var(--color-bg-subtle)] rounded'></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4'>
        <p className='text-sm text-red-700 dark:text-red-400'>Failed to load Key Vault references: {error.message}</p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* Key Vault URL Info */}
      <div className='bg-[var(--color-bg-card)] rounded-lg shadow-sm border border-[var(--color-border)]'>
        <div className='px-6 py-4 border-b border-[var(--color-border)]'>
          <h3 className='text-lg font-medium text-[var(--color-text-primary)]'>Key Vault Configuration</h3>
          <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
            Secrets are stored in Azure Key Vault. Only references are stored in the database.
          </p>
        </div>
        <div className='px-6 py-4'>
          <div className='flex items-center justify-between'>
            <div>
              <label className='block text-sm font-medium text-[var(--color-text-secondary)] mb-1'>Key Vault URL</label>
              <div className='text-sm text-[var(--color-text-primary)] font-mono bg-[var(--color-bg-subtle)] px-3 py-2 rounded border border-[var(--color-border)]'>
                {keyVaultUrl || <span className='text-[var(--color-text-secondary)] italic'>Not configured</span>}
              </div>
            </div>
            <div className='flex items-center space-x-2'>
              <span className='text-sm text-[var(--color-text-secondary)]'>Managed Identity:</span>
              <span className='inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'>
                ✓ Enabled
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Secret References */}
      <div className='bg-[var(--color-bg-card)] rounded-lg shadow-sm border border-[var(--color-border)]'>
        <div className='px-6 py-4 border-b border-[var(--color-border)] flex items-center justify-between'>
          <div>
            <h3 className='text-lg font-medium text-[var(--color-text-primary)]'>Secret References</h3>
            <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
              Configure which secrets should be retrieved from Key Vault
            </p>
          </div>
          <div className='flex items-center space-x-2'>
            <button
              onClick={handleValidateAll}
              disabled={validateMutation.isPending || references.length === 0}
              className='px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/40 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {validateMutation.isPending ? (
                <span className='flex items-center'>
                  <svg
                    className='animate-spin -ml-1 mr-1.5 h-4 w-4'
                    fill='none'
                    viewBox='0 0 24 24'
                  >
                    <circle
                      className='opacity-25'
                      cx='12'
                      cy='12'
                      r='10'
                      stroke='currentColor'
                      strokeWidth='4'
                    />
                    <path
                      className='opacity-75'
                      fill='currentColor'
                      d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                    />
                  </svg>
                  Validating...
                </span>
              ) : (
                'Validate All'
              )}
            </button>
            <button
              onClick={() => setShowAddForm(true)}
              className='px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700'
            >
              + Add Reference
            </button>
          </div>
        </div>

        {/* References Table */}
        <DualScrollTable>
          <table className='min-w-full divide-y divide-[var(--color-border)]'>
            <thead className='bg-[var(--color-bg-subtle)]'>
              <tr>
                <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                  Purpose
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                  Secret Name
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                  Description
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                  Required
                </th>
                <th className='px-6 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                  Status
                </th>
                <th className='px-6 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className='bg-[var(--color-bg-card)] divide-y divide-[var(--color-border)]'>
              {references.length === 0 ? (
                <tr>
                  <td colSpan={6} className='px-6 py-8 text-center text-sm text-[var(--color-text-secondary)]'>
                    No secret references configured. Add references to secrets stored in Key Vault.
                  </td>
                </tr>
              ) : (
                pageItems.map((ref) => (
                  <tr key={ref.purpose} className='hover:bg-[var(--color-bg-subtle)]'>
                    <td className='px-6 py-4 text-sm font-medium text-[var(--color-text-primary)]'>{ref.purpose}</td>
                    <td className='px-6 py-4 text-sm text-[var(--color-text-secondary)] font-mono'>{ref.secretName}</td>
                    <td className='px-6 py-4 text-sm text-[var(--color-text-secondary)]'>
                      {ref.description || <span className='text-[var(--color-text-secondary)]'>-</span>}
                    </td>
                    <td className='px-6 py-4'>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                          ref.isRequired ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
                        }`}
                      >
                        {ref.isRequired ? 'Required' : 'Optional'}
                      </span>
                    </td>
                    <td className='px-6 py-4'>
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(ref.validationStatus)}`}
                      >
                        {getStatusIcon(ref.validationStatus)} {ref.validationStatus}
                      </span>
                      {ref.lastValidated && (
                        <p className='text-xs text-[var(--color-text-secondary)] mt-1'>
                          {formatEastern(ref.lastValidated)}
                        </p>
                      )}
                    </td>
                    <td className='px-6 py-4 text-right'>
                      <button
                        onClick={() => handleDeleteReference(ref.purpose)}
                        disabled={deleteMutation.isPending}
                        className='text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium'
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </DualScrollTable>
        <PaginationControls
          page={page}
          pageSize={pageSize}
          total={total}
          totalPages={totalPages}
          start={start}
          shown={pageItems.length}
          onPage={setPage}
          onPageSize={changeSize}
        />
      </div>

      {/* Add Reference Modal */}
      {showAddForm && (
        <Modal
          isOpen={showAddForm}
          onClose={() => {
            setShowAddForm(false);
            setFormError(null);
            setNewReference({
              purpose: '',
              secretName: '',
              description: '',
              isRequired: false,
            });
          }}
          title='Add Secret Reference'
          size='sm'
          footer={
            <>
              <Button
                variant='outline'
                onClick={() => {
                  setShowAddForm(false);
                  setFormError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                form='add-keyvault-reference-form'
                loading={upsertMutation.isPending}
                disabled={upsertMutation.isPending}
              >
                {upsertMutation.isPending ? 'Adding...' : 'Add Reference'}
              </Button>
            </>
          }
        >
          <form
            id='add-keyvault-reference-form'
            onSubmit={(e) => {
              e.preventDefault();
              handleAddReference();
            }}
            className='space-y-4'
          >
            {formError && (
              <div className='bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded-md text-sm'>
                {formError}
              </div>
            )}

            {/* Purpose */}
            <div>
              <label className='block text-sm font-medium text-[var(--color-text-primary)] mb-1'>
                Purpose <span className='text-red-500'>*</span>
              </label>
              <select
                value={newReference.purpose}
                onChange={(e) => {
                  const purpose = e.target.value;
                  const preset = COMMON_SECRET_PURPOSES.find((p) => p.value === purpose);
                  setNewReference((prev) => ({
                    ...prev,
                    purpose,
                    isRequired: preset?.required ?? prev.isRequired,
                  }));
                }}
                className='w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]'
              >
                <option value=''>Select a purpose...</option>
                {COMMON_SECRET_PURPOSES.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
                <option value='custom'>Custom...</option>
              </select>
              {newReference.purpose === 'custom' && (
                <input
                  type='text'
                  placeholder='Enter custom purpose'
                  className='w-full px-3 py-2 mt-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]'
                  onChange={(e) =>
                    setNewReference((prev) => ({ ...prev, purpose: e.target.value }))
                  }
                />
              )}
            </div>

            {/* Secret Name */}
            <div>
              <label className='block text-sm font-medium text-[var(--color-text-primary)] mb-1'>
                Secret Name in Key Vault <span className='text-red-500'>*</span>
              </label>
              <input
                type='text'
                value={newReference.secretName}
                onChange={(e) =>
                  setNewReference((prev) => ({ ...prev, secretName: e.target.value }))
                }
                placeholder='my-secret-name'
                className='w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]'
              />
              <p className='mt-1 text-xs text-[var(--color-text-secondary)]'>
                Alphanumeric characters and dashes only (1-127 characters)
              </p>
            </div>

            {/* Description */}
            <div>
              <label className='block text-sm font-medium text-[var(--color-text-primary)] mb-1'>Description</label>
              <input
                type='text'
                value={newReference.description}
                onChange={(e) =>
                  setNewReference((prev) => ({ ...prev, description: e.target.value }))
                }
                placeholder='Optional description'
                className='w-full px-3 py-2 border border-[var(--color-border)] bg-[var(--color-bg-card)] text-[var(--color-text-primary)] rounded-md focus:outline-none focus:ring-2 focus:ring-[var(--color-accent)]'
              />
            </div>

            {/* Required */}
            <div className='flex items-center'>
              <input
                type='checkbox'
                id='isRequired'
                checked={newReference.isRequired}
                onChange={(e) =>
                  setNewReference((prev) => ({ ...prev, isRequired: e.target.checked }))
                }
                className='h-4 w-4 text-[var(--color-accent)] border-[var(--color-border)] rounded'
              />
              <label htmlFor='isRequired' className='ml-2 text-sm text-[var(--color-text-primary)]'>
                Required for backup operations
              </label>
            </div>
          </form>
        </Modal>
      )}

      {/* Security Note */}
      <div className='bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4'>
        <div className='flex'>
          <div className='flex-shrink-0'>
            <svg className='h-5 w-5 text-yellow-400' viewBox='0 0 20 20' fill='currentColor'>
              <path
                fillRule='evenodd'
                d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                clipRule='evenodd'
              />
            </svg>
          </div>
          <div className='ml-3'>
            <h3 className='text-sm font-medium text-yellow-800 dark:text-yellow-300'>Security Notice</h3>
            <p className='mt-1 text-sm text-yellow-700 dark:text-yellow-400'>
              No actual secrets are stored in the database. Only references to secrets in Azure Key
              Vault are stored. The application uses Managed Identity to securely access Key Vault
              at runtime.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default KeyVaultReferencesForm;
